import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AccountPlan } from '../../../accounting/model/account-plan';
import { AccountPlanService } from '../../../accounting/service/data/account-plan.service';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { SelectionItem, ValueTFn } from '../../../core/component/select/select.component';
import { standardCommand } from '../../../core/model/command';
import { CurrencySymbol, Money } from '../../../core/model/money';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormCommandHandlerResolver } from '../../../core/service/command/form-command-handler-resolver.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { Article } from '../../model/article';
import {
  AdjustmentKind,
  AdjustmentKindToString,
  ArticleTimeUnitKind,
  ArticleTimeUnitKindToString,
  ArticleUnit,
  ArticleUnitKindToString,
} from '../../model/const/enums';
import { ArticleGroupService } from '../../service/data/article-group.service';
import { ArticleService } from '../../service/data/article.service';
import { CompanyService } from '../../service/data/company.service';
import { DimensionLabelsService } from '../../../accounting/service/data/dimension-labels.service';
import { LanguageService } from '../../../core/service/language.service';

@Component({
    selector: 'app-article-form',
    templateUrl: './article-form.component.html',
    styleUrls: ['./article-form.component.scss'],
    providers: [DeleteFormCommandHandler, FormCommandHandlerResolver, { provide: DataService, useExisting: ArticleService }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ArticleFormComponent extends FormComponentBase<Article> {
  constructor(
    private _fb: FormBuilder,
    private _fc: FormControlExFactory,
    private _accountPlanService: AccountPlanService,
    readonly articleGroupService: ArticleGroupService,
    private readonly _dimensionLabelsService: DimensionLabelsService,
    private readonly _languageService: LanguageService,
    activatedRoute: ActivatedRoute,
    commandDispatcher: CommandDispatcher,
    handlerResolver: FormCommandHandlerResolver,
    service: ArticleService,
    private _companyService: CompanyService,
    messageService: MessageService,
    navigation: NavigationService
  ) {
    super(activatedRoute, service, messageService, navigation, commandDispatcher, handlerResolver);
    this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
  }

  // None, Free, Negotiation
  isAdjustmentDisabled_OpenGroup: ValueTFn<boolean | null> = () => {
    const isUsed = this.dataContext?.articleUsed;
    const a = this.dataContext.adjustment;
    const isGroup1 = a === AdjustmentKind.None || a === AdjustmentKind.Free || a === AdjustmentKind.Negotiation;
    return this.isNew || !isUsed || isGroup1 ? null : true;
  };

  // Index, FixedPercentage, FreeToBrf
  isAdjustmentDisabled_LockedGroup: ValueTFn<boolean | null> = () => {
    const isUsed = this.dataContext?.articleUsed;
    return this.isNew || !isUsed ? null : true;
  };

  adjustments = [
    new SelectionItem(AdjustmentKindToString(AdjustmentKind.None), AdjustmentKind.None, this.isAdjustmentDisabled_OpenGroup),
    new SelectionItem(AdjustmentKindToString(AdjustmentKind.Free), AdjustmentKind.Free, this.isAdjustmentDisabled_OpenGroup),
    new SelectionItem(AdjustmentKindToString(AdjustmentKind.Negotiation), AdjustmentKind.Negotiation, this.isAdjustmentDisabled_OpenGroup),
    new SelectionItem(AdjustmentKindToString(AdjustmentKind.Index), AdjustmentKind.Index, this.isAdjustmentDisabled_LockedGroup),
    new SelectionItem(
      AdjustmentKindToString(AdjustmentKind.FixedPercentage),
      AdjustmentKind.FixedPercentage,
      this.isAdjustmentDisabled_LockedGroup
    ),
    new SelectionItem(AdjustmentKindToString(AdjustmentKind.FeeToBrf), AdjustmentKind.FeeToBrf, this.isAdjustmentDisabled_LockedGroup),
  ];

  accountPlans: AccountPlan[];
  claimAccounts: { codePlanId: string; accounts: SelectionItem<string>[] }[] = [];
  incomeAccounts: { codePlanId: string; accounts: SelectionItem<string>[] }[] = [];
  vatAccounts: { codePlanId: string; accounts: SelectionItem<string>[] }[] = [];
  advancePaymentRentAccounts: { codePlanId: string; accounts: SelectionItem<string>[] }[] = [];
  rentLossAccounts: { codePlanId: string; accounts: SelectionItem<string>[] }[] = [];
  rentLossIncomeAccounts: { codePlanId: string; accounts: SelectionItem<string>[] }[] = [];

  get codingForms(): FormGroup[] {
    return (this.form?.controls['codings'] as FormArray)?.controls.map(c => c as FormGroup);
  }

  getClaimAccountSelection(codePlanId: string): SelectionItem<string>[] {
    return this.claimAccounts.find(a => a.codePlanId === codePlanId).accounts;
  }
  getIncomeAccountSelection(codePlanId: string): SelectionItem<string>[] {
    return this.incomeAccounts.find(a => a.codePlanId === codePlanId).accounts;
  }
  getRentLossAccountSelection(codePlanId: string): SelectionItem<string>[] {
    return this.rentLossAccounts.find(a => a.codePlanId === codePlanId).accounts;
  }
  getRentLossIncomeAccountSelection(codePlanId: string): SelectionItem<string>[] {
    return this.rentLossIncomeAccounts.find(a => a.codePlanId === codePlanId).accounts;
  }
  getVatAccountSelection(codePlanId: string): SelectionItem<string>[] {
    return this.vatAccounts.find(a => a.codePlanId === codePlanId).accounts;
  }
  getAdvancePaymentRentAccountSelection(codePlanId: string): SelectionItem<string>[] {
    return this.advancePaymentRentAccounts.find(a => a.codePlanId === codePlanId).accounts;
  }

  getAccountPlan(accountPlanId: string): AccountPlan | undefined {
    return this.accountPlans?.find(plan => plan.id === accountPlanId);
  }

  showArticleCostPlace(accountPlanId: string): boolean {
    const accountPlan = this.getAccountPlan(accountPlanId);
    return !!accountPlan?.enableCostPlace && !!accountPlan?.enableArticleCostPlace;
  }

  showArticleFunction(accountPlanId: string): boolean {
    const accountPlan = this.getAccountPlan(accountPlanId);
    return !!accountPlan?.enableFunction;
  }

  timeUnits: SelectionItem<ArticleTimeUnitKind>[] = [
    { name: ArticleTimeUnitKindToString(ArticleTimeUnitKind.Hour), value: ArticleTimeUnitKind.Hour },
    { name: ArticleTimeUnitKindToString(ArticleTimeUnitKind.Day), value: ArticleTimeUnitKind.Day },
    { name: ArticleTimeUnitKindToString(ArticleTimeUnitKind.Month), value: ArticleTimeUnitKind.Month },
    { name: ArticleTimeUnitKindToString(ArticleTimeUnitKind.Quarter), value: ArticleTimeUnitKind.Quarter },
    { name: ArticleTimeUnitKindToString(ArticleTimeUnitKind.Year), value: ArticleTimeUnitKind.Year },
  ];

  units: SelectionItem<ArticleUnit>[] = [
    { name: ArticleUnitKindToString(ArticleUnit.None), value: ArticleUnit.None },
    { name: ArticleUnitKindToString(ArticleUnit.Units), value: ArticleUnit.Units },
    { name: ArticleUnitKindToString(ArticleUnit.Kg), value: ArticleUnit.Kg },
    { name: ArticleUnitKindToString(ArticleUnit.Gram), value: ArticleUnit.Gram },
    { name: ArticleUnitKindToString(ArticleUnit.Kwh), value: ArticleUnit.Kwh },
    { name: ArticleUnitKindToString(ArticleUnit.Ton), value: ArticleUnit.Ton },
    { name: ArticleUnitKindToString(ArticleUnit.Km), value: ArticleUnit.Km },
    { name: ArticleUnitKindToString(ArticleUnit.M2), value: ArticleUnit.M2 },
    { name: ArticleUnitKindToString(ArticleUnit.M3), value: ArticleUnit.M3 },
    { name: ArticleUnitKindToString(ArticleUnit.Litre), value: ArticleUnit.Litre },
  ];

  vatPercentages: SelectionItem<number>[] = [
    { name: 'ARTICLE.NO_VAT', value: 0 },
    { name: '25%', value: 25 },
    { name: '12%', value: 12 },
    { name: '6%', value: 6 },
  ];

  get vatEnabled(): boolean {
    return this.form?.controls['vatPercent'].value > 0;
  }
  protected createModel(): Article {
    return new Article();
  }

  get isPropertyTaxEnabled(): boolean {
    return this.form?.controls['isPropertyTax'].value;
  }

  protected getFormGroup(): FormGroup {
    return this._fb.group({
      adjustment: ValueTFn.valueOf(this.isAdjustmentDisabled_OpenGroup)
        ? this._fc.createReadonly('ARTICLE.ADJUSTMENT', this.dataContext.adjustment)
        : this._fc.create('ARTICLE.ADJUSTMENT', this.dataContext.adjustment),
      code: this._fc.createWithOptions('ARTICLE.CODE', this.dataContext.code, { length: 10, required: true }),
      caption: this._fc.createWithOptions('ARTICLE.CAPTION', this.dataContext.caption, { length: 40, required: true }),
      includeInContractualRent: this._fc.create('ARTICLE.INCLUDE_IN_CONTRACTUAL_RENT', this.dataContext.includeInContractualRent),
      invoiceTexts: this._fc.createWithOptions('ARTICLE.INVOICE_TEXT', this.dataContext.invoiceTexts, { length: 255 }),
      dwellingCategory:
        !this.dataContext.allowDisableDwelling && this.dataContext.dwellingCategory
          ? this._fc.createReadonly('ARTICLE.RENTAL_OBJECT_CATEGORY_DWELLING', this.dataContext.dwellingCategory)
          : this._fc.create('ARTICLE.RENTAL_OBJECT_CATEGORY_DWELLING', this.dataContext.dwellingCategory),
      otherCategory:
        !this.dataContext.allowDisableOtherRentalObject && this.dataContext.otherCategory
          ? this._fc.createReadonly('ARTICLE.RENTAL_OBJECT_CATEGORY_OTHER', this.dataContext.otherCategory)
          : this._fc.create('ARTICLE.RENTAL_OBJECT_CATEGORY_OTHER', this.dataContext.otherCategory),
      parkingCategory:
        !this.dataContext.allowDisableParking && this.dataContext.parkingCategory
          ? this._fc.createReadonly('ARTICLE.RENTAL_OBJECT_CATEGORY_PARKING', this.dataContext.parkingCategory)
          : this._fc.create('ARTICLE.RENTAL_OBJECT_CATEGORY_PARKING', this.dataContext.parkingCategory),
      premisesCategory:
        !this.dataContext.allowDisablePremises && this.dataContext.premisesCategory
          ? this._fc.createReadonly('ARTICLE.RENTAL_OBJECT_CATEGORY_PREMISES', this.dataContext.premisesCategory)
          : this._fc.create('ARTICLE.RENTAL_OBJECT_CATEGORY_PREMISES', this.dataContext.premisesCategory),
      price: this._fc.create(
        'ARTICLE.PRICE',
        this.dataContext.price ? this.dataContext.price : new Money(this.dataContext.currencyCode, null)
      ),
      invoiceRowSortOrder: this._fc.createMandatory('ARTICLE.INVOICE_ROW_SORT_ORDER', this.dataContext.invoiceRowSortOrder),
      unit: this._fc.create('ARTICLE.UNIT', this.dataContext.unit),
      timeUnit: this._fc.createMandatory('ARTICLE.TIME_UNIT', this.dataContext.timeUnit),
      vatPercent: this._fc.create('ARTICLE.VAT_PERCENT', this.dataContext.vatPercent),
      voluntaryVat: this._fc.create('ARTICLE.VOLUNTARY_VAT', this.dataContext.voluntaryVat, null, null, 'ARTICLE.HELP_TEXT.VOLUNTARY_VAT'),
      articleGroupId: this._fc.createMandatory('ARTICLE_GROUP.ARTICLE_GROUP', this.dataContext.articleGroupId),
      currencyCode: this._fc.createMandatory('COMMON.CURRENCY', this.dataContext.currencyCode),
      bookTurnoverOnInvoiceDate: this._fc.create(
        'ARTICLE.BOOK_TURNOVER_ON_INVOICE_DATE',
        this.dataContext.bookTurnoverOnInvoiceDate,
        null,
        null,
        'ARTICLE.HELP_TEXT.BOOK_TURNOVER_ON_INVOICE_DATE'
      ),
      favorite: this._fc.create('ARTICLE.FAVORITE', this.dataContext.favorite, null, null, 'ARTICLE.HELP_TEXT.FAVORITE'),
      isPropertyTax: this._fc.create(
        'PROPERTY_TAX.IS_PROPERTY_TAX',
        this.dataContext.isPropertyTax,
        null,
        null,
        'PROPERTY_TAX.HELP_TEXT.IS_PROPERTY_TAX'
      ),
      isRetroactiveForPropertyTax: this._fc.create(
        'PROPERTY_TAX.IS_RETROACTIVE_FOR_PROPERTY_TAX',
        this.dataContext.isRetroactiveForPropertyTax,
        null,
        null,
        'PROPERTY_TAX.HELP_TEXT.IS_RETROACTIVE_FOR_PROPERTY_TAX'
      ),
      isReduction: this._fc.create('ARTICLE.IS_REDUCTION', this.dataContext.isReduction, null, null, 'ARTICLE.HELP_TEXT.IS_REDUCTION'),
      codings: this._fb.array(this.createCodingForms()),
    });
  }

  createDefaultCoding() {
    let forms = this.form.get('codings') as FormArray;
    for (let accountPlan of this.accountPlans) {
      forms.push(
        this.createCodingForm(
          accountPlan.name,
          accountPlan.id,
          accountPlan.customerClaimAccount,
          accountPlan.incomeAccount,
          null,
          null,
          null,
          null,
          null,
          null
        )
      );
    }
  }

  createCodingForm(
    accountPlanName,
    accountPlanId: string,
    claimAccountId: string,
    incomeAccountId: string,
    rentLossAccountId: string,
    rentLossIncomeAccountId: string,
    incomePeriodizationAccountId: string,
    vatAccountId: string,
    costPlace: string,
    func: string
  ): FormGroup {
    const labels = this._dimensionLabelsService.fromAccountPlan(this.getAccountPlan(accountPlanId));
    return this._fb.group({
      accountPlanName: accountPlanName,
      accountPlanId: accountPlanId,
      claimAccountId: this._fc.create('COMMON.CLAIM_ACCOUNT', claimAccountId),
      incomeAccountId: this._fc.create('COMMON.INCOME_ACCOUNT', incomeAccountId),
      costPlace: this._fc.createWithOptions(labels.costPlaceLabel, costPlace, {
        length: 10,
        helpText: this._languageService.getText('ARTICLE.HELP_TEXT.ACCOUNT_PLAN_COST_PLACE', {
          dimension: labels.costPlaceLabel,
        }),
      }),
      function: this._fc.createWithOptions(labels.functionLabel, func, {
        length: 10,
        helpText: this._languageService.getText('ARTICLE.HELP_TEXT.ACCOUNT_PLAN_FUNCTION', {
          dimension: labels.functionLabel,
        }),
      }),
      rentLossAccountId: this._fc.create('COMMON.RENT_LOSS_ACCOUNT', rentLossAccountId),
      rentLossIncomeAccountId: this._fc.create(
        'COMMON.RENT_LOSS_INCOME_ACCOUNT_DETAILED',
        rentLossIncomeAccountId,
        null,
        null,
        'COMMON.HELP_TEXT.RENT_LOSS_INCOME_ACCOUNT'
      ),
      incomePeriodizationAccountId: this._fc.create(
        'COMMON.INCOME_PERIODIZATION_ACCOUNT',
        incomePeriodizationAccountId,
        null,
        null,
        'COMMON.HELP_TEXT.INCOME_PERIODIZATION_ACCOUNT'
      ),
      vatAccountId: this._fc.create('COMMON.VAT_ACCOUNT', vatAccountId),
    });
  }

  createCodingForms(): FormGroup[] {
    if (this.isNew) {
      return [];
    }
    let forms: FormGroup[] = [];
    for (let coding of this.dataContext.codings) {
      forms.push(
        this.createCodingForm(
          coding.accountPlanName,
          coding.accountPlanId,
          coding.claimAccountId,
          coding.incomeAccountId,
          coding.rentLossAccountId,
          coding.rentLossIncomeAccountId,
          coding.incomePeriodizationAccountId,
          coding.vatAccountId,
          coding.costPlace,
          coding.function
        )
      );
    }
    return forms;
  }

  currencySelection: SelectionItem<string>[];

  protected init(data: Article) {
    super.init(data);

    this.AddSubscription(
      this._accountPlanService.getExtensiveList().subscribe(accountPlans => {
        this.claimAccounts = accountPlans.map(plan => {
          return { codePlanId: plan.id, accounts: plan.claimAccounts.map(a => new SelectionItem(a.toString(), a.id)) };
        });
        this.incomeAccounts = accountPlans.map(plan => {
          return { codePlanId: plan.id, accounts: plan.incomeAccounts.map(a => new SelectionItem(a.toString(), a.id)) };
        });
        this.rentLossAccounts = accountPlans.map(plan => {
          let planAccounts = { codePlanId: plan.id, accounts: plan.incomeAccounts.map(a => new SelectionItem(a.toString(), a.id)) };
          planAccounts.accounts.splice(0, 0, new SelectionItem('ARTICLE.NO_RENTLOSS', null));
          return planAccounts;
        });
        this.rentLossIncomeAccounts = accountPlans.map(plan => {
          let planAccounts = { codePlanId: plan.id, accounts: plan.incomeAccounts.map(a => new SelectionItem(a.toString(), a.id)) };
          planAccounts.accounts.splice(0, 0, new SelectionItem('NOT_SELECTED_TEXT', null));
          return planAccounts;
        });
        this.vatAccounts = accountPlans.map(plan => {
          return { codePlanId: plan.id, accounts: plan.vatAccounts.map(a => new SelectionItem(a.toString(), a.id)) };
        });
        this.advancePaymentRentAccounts = accountPlans.map(plan => {
          let planAccounts = {
            codePlanId: plan.id,
            accounts: plan.advancePaymentRentAccounts.map(a => new SelectionItem(a.toString(), a.id)),
          };
          planAccounts.accounts.splice(0, 0, new SelectionItem('ARTICLE.NO_PERIODIZATION', null));
          return planAccounts;
        });
        this.accountPlans = accountPlans;
        this.codingForms?.forEach(form => {
          const labels = this._dimensionLabelsService.fromAccountPlan(this.getAccountPlan(form.get('accountPlanId').value));
          this._dimensionLabelsService.setControlCaption(form.get('costPlace'), labels.costPlaceLabel);
          this._dimensionLabelsService.setControlCaption(form.get('function'), labels.functionLabel);
        });

        if (this.dataContext.isNew) {
          this.createDefaultCoding();
        }
      })
    );

    this.AddSubscription(
      this._companyService.getUsedCurrencies().subscribe(res => {
        this.currencySelection = res.map(c => new SelectionItem(CurrencySymbol(c), c));
        if (this.isNew) {
          this.form.controls['currencyCode'].setValue(this.currencySelection[0].value);
        }
      })
    );

    this.AddSubscription(
      this.form.controls['currencyCode'].valueChanges.subscribe(val => {
        this.dataContext.currencyCode = val;
        if (this.form.controls['price'].value) {
          this.form.controls['price'].setValue(new Money(val, this.form.controls['price'].value.amount));
        }
      })
    );

    this.form.controls['adjustment'].valueChanges.subscribe((value: AdjustmentKind) => {
      if (value === AdjustmentKind.Index || value === AdjustmentKind.FixedPercentage) {
        this.form.controls['timeUnit'].setValue(ArticleTimeUnitKind.Year);
        this.dataContext.timeUnit = ArticleTimeUnitKind.Year;
        this.form.controls['timeUnit'].disable();
        this.form.controls['unit'].setValue(ArticleUnit.None);
        this.form.controls['unit'].disable();
      } else {
        this.form.controls['timeUnit'].enable();
        this.form.controls['unit'].enable();
      }
    });

    this.form.controls['vatPercent'].valueChanges.subscribe((value: number) => {
      this.setVatAccount();
    });

    this.form.controls['vatPercent'].setValue(this.dataContext.vatPercent);
    this.form.controls['adjustment'].setValue(this.dataContext.adjustment);
  }

  setVatAccount() {
    if (!this.accountPlans) {
      return;
    }

    for (let form of this.codingForms) {
      let accountPlan = this.accountPlans.find(p => p.id === form.get('accountPlanId').value);
      let vatAccount = form.get('vatAccountId');

      vatAccount.enable();

      switch (this.form.get('vatPercent').value) {
        case 0:
          vatAccount.setValue(null);
          vatAccount.disable();
          break;

        case 6:
          vatAccount.setValue(accountPlan.vat6pctAccount);
          break;

        case 12:
          vatAccount.setValue(accountPlan.vat12pctAccount);
          break;

        case 25:
          vatAccount.setValue(accountPlan.vat25pctAccount);
          break;
      }
    }
  }
}
